import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import {
  useCreateMedia,
  useDeleteMedia
} from "../../../api/medias/service/index";
import { Modal, ModalBody, Button, Progress } from "reactstrap";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { getTheme2FormFieldsCss } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { useTranslation } from 'react-i18next';
import { formatDateTimeForDisplay } from "../../../utils/dateUtils";
import { copyTextToClipboard } from "../../../utils/clipboard";
import { useNotification } from "contexts/NotificationContext";
import { toAppMediaAbsoluteUrl, toUploadRelativePath } from "utils/mediaUrl";
import useSecureMediaUrl from "hooks/useSecureMediaUrl";
import { createExternalMediaShareUrl, fetchSecureMediaBlobUrl } from "api/secureMedia";

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "tif", "tiff", "webp"]);
const getExtension = (fileName = "") =>
  String(fileName).split(".").pop()?.toLowerCase() || "";
const isImageFile = (fileName) => IMAGE_EXTENSIONS.has(getExtension(fileName));
const isPdfFile = (fileName) => getExtension(fileName) === "pdf";
const isPreviewableFile = (fileName) => isImageFile(fileName) || isPdfFile(fileName);
const getMediaUploadPath = (media) => toUploadRelativePath(media?.media?.url || "");
const PDF_WORKER_URL =
  "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

const FilesPdfViewer = ({ fileUrl, isDarkMode }) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  if (!fileUrl) {
    return (
      <div style={{ color: "#fff", padding: 24, textAlign: "center" }}>
        PDF URL is missing
      </div>
    );
  }

  return (
    <Worker workerUrl={PDF_WORKER_URL}>
      <div style={{ height: "100%", overflow: "auto" }}>
        <Viewer
          fileUrl={fileUrl}
          theme={isDarkMode ? "dark" : "light"}
          defaultScale={1}
          plugins={[defaultLayoutPluginInstance]}
        />
      </div>
    </Worker>
  );
};

const FilesLightboxBody = ({ item, isDarkMode, t, onBlobUrlChange }) => {
  const { blobUrl, isLoading, error } = useSecureMediaUrl(item?.uploadPath, {
    enabled: !!item?.uploadPath,
  });

  useEffect(() => {
    onBlobUrlChange?.(blobUrl || "");
    return () => onBlobUrlChange?.("");
  }, [blobUrl, onBlobUrlChange]);

  if (isLoading) {
    return (
      <div style={{ color: "#fff", textAlign: "center" }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">{t("common.loading", "Loading...")}</span>
        </div>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div style={{ color: "#fff", padding: 24, textAlign: "center" }}>
        {t("filesTab.previewFailed", "Could not load this file.")}
      </div>
    );
  }

  if (item.kind === "pdf") {
    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "90vw",
          height: "85vh",
          background: isDarkMode ? "#1e1e2d" : "#fff",
          overflow: "hidden",
        }}
      >
        <FilesPdfViewer key={blobUrl} fileUrl={blobUrl} isDarkMode={isDarkMode} />
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={item.name || "Preview"}
      onClick={(e) => e.stopPropagation()}
      style={{
        maxWidth: "90vw",
        maxHeight: "85vh",
        objectFit: "contain",
        background: "#111",
      }}
    />
  );
};

const FilesTab = ({ permission, data, id, isLoading, activeColor = "blue", module, type, isTheme2 = false }) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileDescription, setFileDescription] = useState('');
  const fileInputRef = useRef(null);
  const { showNotification } = useNotification();
  
  // Theme detection with state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    if (darkMode != null) return JSON.parse(darkMode);
    return !document.body.classList.contains("white-content");
  });

  useEffect(() => {
    const checkTheme = () => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme =
        darkMode != null
          ? JSON.parse(darkMode)
          : !document.body.classList.contains("white-content");
      setIsDarkMode((prev) => (prev !== newTheme ? newTheme : prev));
    };

    checkTheme();
    window.addEventListener("storage", checkTheme);

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const interval = setInterval(checkTheme, 800);

    return () => {
      window.removeEventListener("storage", checkTheme);
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  // Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [lightboxBlobUrl, setLightboxBlobUrl] = useState("");
  const lightboxOpen = lightboxIndex != null;

  // Mutations
  const createMediaMutation = useCreateMedia();
  const deleteMediaMutation = useDeleteMedia();

  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".files-tab.files-tab--theme2",
            fieldsSelector: ".files-tab-fields",
          })
        : "",
    [isTheme2]
  );

  const inputStyle = useMemo(
    () => (isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined),
    [isTheme2]
  );

  const fileTextStyles = useMemo(
    () => ({
      name: isDarkMode ? "#ffffff" : "#212529",
      info: isDarkMode ? "rgba(255, 255, 255, 0.75)" : "#6c757d",
      heading: isDarkMode ? "#ffffff" : "#212529",
      label: isDarkMode ? "rgba(255, 255, 255, 0.95)" : "#495057",
      muted: isDarkMode ? "rgba(255, 255, 255, 0.6)" : "#6c757d",
      subtle: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#adb5bd",
      icon: isDarkMode ? "#ffffff" : "#212529",
      previewCardBg: isDarkMode ? "#27293d" : "#f8f9fa",
      previewCardBorder: isDarkMode
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid #dee2e6",
    }),
    [isDarkMode]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const maxFileSize = 50 * 1024 * 1024; // 50MB for files (larger than images)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/tiff',
      'image/webp',
    ];

    const processFiles = async () => {
      const processedFiles = [];

      for (const file of files) {
        // Check file type
        const typeValid = allowedTypes.includes(file.type);

        // Check file size
        const sizeValid = file.size <= maxFileSize;

        // Add validation info to file object
        file.validation = {
          typeValid,
          sizeValid
        };

        processedFiles.push(file);
      }

      setUploadedFiles(prev => [...prev, ...processedFiles]);
    };

    processFiles();
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = [t('filesTab.bytes'), t('filesTab.kb'), t('filesTab.mb'), t('filesTab.gb')];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeName = (fileType) => {
    const typeMap = {
      'application/pdf': t('filesTab.pdf'),
      'application/msword': t('filesTab.doc'),
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': t('filesTab.docx'),
      'application/vnd.ms-excel': t('filesTab.xls'),
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': t('filesTab.xlsx'),
      'text/plain': t('filesTab.txt'),
      'image/png': t('filesTab.png'),
      'image/jpeg': t('filesTab.jpg'),
      'image/jpg': t('filesTab.jpg'),
      'image/gif': t('filesTab.gif'),
      'image/tiff': t('filesTab.tiff'),
      'image/webp': t('filesTab.webp'),
    };
    return typeMap[fileType] || t('filesTab.unknown');
  };

  const handleUpload = async () => {
    if (uploadedFiles.length === 0) return;

    // Filter only valid files for upload
    const validFiles = uploadedFiles.filter(file =>
      file.validation?.typeValid &&
      file.validation?.sizeValid
    );

    if (validFiles.length === 0) {
      console.log('No valid files to upload');
      return;
    }

    try {
      const mediaData = {
        [module === "deals"
          ? "deal"
          : module === "customers"
            ? "customer"
            : module === "accounts"
              ? "calculus"
              : module === "channels"
                ? "channel"
                : "inventory"]: id,
        type: type,
        description: fileDescription || `Auto Link invoice`,
        module: module,
        created_date: new Date().toISOString(),
      };

      const files = {
        media: validFiles
      };

      await createMediaMutation.mutateAsync({ data: mediaData, files });

      // Clear uploaded files after successful upload
      setUploadedFiles([]);
      setFileDescription('');
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    setSelectedDeleteId(mediaId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedDeleteId) {
      try {
        await deleteMediaMutation.mutateAsync({ id: selectedDeleteId });
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    }
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const getFileIcon = (fileName) => {
    const extension = getExtension(fileName);
    switch (extension) {
      case 'pdf':
        return 'fas fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'tim-icons icon-notes';
      case 'xls':
      case 'xlsx':
        return 'tim-icons icon-chart-bar-32';
      case 'txt':
        return 'tim-icons icon-notes';
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'tif':
      case 'tiff':
      case 'webp':
        return 'tim-icons icon-image-02';
      default:
        return 'tim-icons icon-paper';
    }
  };

  const getFileIconColor = (fileName) => {
    if (isPdfFile(fileName)) return '#dc3545';
    return fileTextStyles.icon;
  };

  const previewableFiles = useMemo(
    () =>
      (data || [])
        .filter((media) => isPreviewableFile(media?.media?.name || "") && media?.media?.url)
        .map((media) => ({
          id: media.id,
          name: media.media?.name || "",
          uploadPath: getMediaUploadPath(media),
          kind: isPdfFile(media.media?.name) ? "pdf" : "image",
        }))
        .filter((item) => !!item.uploadPath),
    [data]
  );

  const openLightbox = useCallback(
    (media) => {
      const index = previewableFiles.findIndex((item) => item.id === media.id);
      if (index < 0) return;
      setLightboxIndex(index);
    },
    [previewableFiles]
  );

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    setLightboxBlobUrl("");
  }, []);

  const handleLightboxDownload = useCallback(() => {
    if (!lightboxBlobUrl) return;
    const name = previewableFiles[lightboxIndex]?.name || "download";
    const link = document.createElement("a");
    link.href = lightboxBlobUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [lightboxBlobUrl, lightboxIndex, previewableFiles]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeLightbox();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setLightboxIndex((prev) =>
          prev == null
            ? prev
            : prev === 0
              ? previewableFiles.length - 1
              : prev - 1
        );
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setLightboxIndex((prev) =>
          prev == null
            ? prev
            : prev === previewableFiles.length - 1
              ? 0
              : prev + 1
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen, previewableFiles.length, closeLightbox]);

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className='sr-only'>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames("files-tab", "row", { "files-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <div className={classNames("w-100", { "files-tab-fields": isTheme2 })} style={{ display: "contents" }}>
      {permission?.add_files && (
        <div className="col-md-4 mb-4">
          <div className="card">
            <div className="card-header font-weight-bold">{t('filesTab.uploadFiles')}</div>
            <div className="card-body">
              <div
                className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: isDragOver
                    ? (isDarkMode ? '2px dashed #e14eca' : '2px dashed #1d8cf8')
                    : (isDarkMode ? '2px dashed #344675' : '2px dashed #6c757d'),
                  padding: '40px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: isDragOver
                    ? (isDarkMode ? '#1e1e2d' : '#e3f2fd')
                    : (isDarkMode ? '#27293d' : '#f8f9fa'),
                  transition: 'all 0.3s ease',
                  marginBottom: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: isDragOver
                    ? (isDarkMode
                      ? '0 2px 8px rgba(225, 78, 202, 0.15), 0 0 0 1px rgba(225, 78, 202, 0.05)'
                      : '0 2px 8px rgba(29, 140, 248, 0.15), 0 0 0 1px rgba(29, 140, 248, 0.05)')
                    : (isDarkMode
                      ? '0 1px 4px rgba(52, 70, 117, 0.1), 0 0 0 1px rgba(52, 70, 117, 0.05)'
                      : '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)')
                }}
              >
                {/* Gradient overlay */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: isDragOver
                    ? (isDarkMode
                      ? 'linear-gradient(135deg, rgba(225, 78, 202, 0.1) 0%, rgba(225, 78, 202, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(29, 140, 248, 0.1) 0%, rgba(29, 140, 248, 0.05) 100%)')
                    : (isDarkMode
                      ? 'linear-gradient(135deg, rgba(52, 70, 117, 0.05) 0%, rgba(52, 70, 117, 0.02) 100%)'
                      : 'linear-gradient(135deg, rgba(108, 117, 125, 0.05) 0%, rgba(108, 117, 125, 0.02) 100%)'),
                  pointerEvents: 'none',
                  boxShadow: isDragOver
                    ? (isDarkMode
                      ? '0 0 8px rgba(225, 78, 202, 0.1)'
                      : '0 0 8px rgba(29, 140, 248, 0.1)')
                    : (isDarkMode
                      ? '0 0 4px rgba(52, 70, 117, 0.05)'
                      : '0 0 4px rgba(108, 117, 125, 0.05)')
                }}></div>

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <i
                    className="ni ni-cloud-upload-94"
                    style={{
                      fontSize: '48px',
                      color: isDragOver ? '#e14eca' : '#1d8cf8',
                      marginBottom: '15px',
                      display: 'block',
                      filter: isDragOver ? 'drop-shadow(0 0 10px rgba(225, 78, 202, 0.5))' : 'none'
                    }}
                  ></i>
                  <i className="tim-icons icon-upload" style={{
                    fontSize: '36px',
                    color: isDarkMode ? '#6c757d' : '#adb5bd',
                    marginBottom: '10px',
                    display: 'block'
                  }}></i>
                  <h6 className="mb-2" style={{ color: fileTextStyles.label, fontWeight: '600' }}>
                    {t('filesTab.uploadFiles')}
                  </h6>
                  <p className="mb-0" style={{ color: fileTextStyles.muted }}>
                    {t('filesTab.dragAndDropFiles')}
                  </p>
                  <div style={{
                    marginTop: '15px',
                    fontSize: '12px',
                    color: fileTextStyles.subtle
                  }}>
                    {t('filesTab.supportsFormats')}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif,.tif,.tiff,.webp"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ color: fileTextStyles.label, fontWeight: '600' }}>{t('filesTab.fileDescription')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder={t('filesTab.enterFileDescription')}
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  style={
                    isTheme2
                      ? inputStyle
                      : {
                          backgroundColor: isDarkMode ? '#27293d' : '#ffffff',
                          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #dee2e6',
                          color: isDarkMode ? '#ffffff' : '#495057',
                        }
                  }
                />
              </div>

              {/* Uploaded Files Preview */}
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  <h6 className="mb-3" style={{ color: fileTextStyles.heading, fontWeight: "600" }}>
                    Selected Files ({uploadedFiles.length})
                  </h6>
                  <div className="row">
                    {uploadedFiles.map((file, index) => {
                      const isValid = file.validation?.typeValid && file.validation?.sizeValid;
                      const hasError = !isValid;

                      return (
                        <div className="col-6 mb-2" key={index}>
                          <div className="card" style={{
                            backgroundColor: fileTextStyles.previewCardBg,
                            border: hasError ? "2px solid #fd5d93" : fileTextStyles.previewCardBorder,
                            overflow: 'hidden',
                            boxShadow: isDarkMode
                              ? '0 1px 4px rgba(52, 70, 117, 0.1), 0 0 0 1px rgba(52, 70, 117, 0.05)'
                              : '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)',
                            transition: 'all 0.3s ease'
                          }}>
                            <div className="card-body p-2 text-center">
                              <i
                                className={getFileIcon(file.name)}
                                style={{
                                  fontSize: '25px',
                                  marginBottom: '8px',
                                  display: 'block',
                                  color: getFileIconColor(file.name)
                                }}
                              ></i>
                              <small
                                className="d-block text-truncate"
                                style={{ color: fileTextStyles.name, fontWeight: 500 }}
                              >
                                {file.name}
                              </small>
                              <small
                                className="d-block"
                                style={{ color: fileTextStyles.info, fontSize: "10px" }}
                              >
                                {formatFileSize(file.size)} • {getFileTypeName(file.type)}
                              </small>
                              {hasError && (
                                <div style={{ marginTop: '5px' }}>
                                  {!file.validation?.typeValid && (
                                    <small className="text-danger d-block" style={{ fontSize: '10px' }}>
                                      <i className='tim-icons icon-alert-circle'></i> {t('filesTab.invalidFileType')}
                                    </small>
                                  )}
                                  {!file.validation?.sizeValid && (
                                    <small className="text-danger d-block" style={{ fontSize: '10px' }}>
                                      <i className='tim-icons icon-alert-circle'></i> {t('filesTab.fileTooLarge')}
                                    </small>
                                  )}
                                </div>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-danger mt-1"
                                onClick={() => removeFile(index)}
                                disabled={createMediaMutation.isLoading || createMediaMutation.isPending}
                                style={{
                                  fontSize: '12px'
                                }}
                              >
                                <i className="tim-icons icon-simple-remove"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Upload Button */}
                  {(() => {
                    const validFiles = uploadedFiles.filter(file =>
                      file.validation?.typeValid &&
                      file.validation?.sizeValid
                    );
                    const hasValidFiles = validFiles.length > 0;
                    const isUploading = createMediaMutation.isLoading || createMediaMutation.isPending;

                    return (
                      <>
                        {isUploading && (
                          <div className="mt-4 mb-2">
                            <div className="text-center mb-2 font-weight-bold text-info" style={{ fontSize: '14px' }}>
                              <i className="tim-icons icon-cloud-upload-94 mr-2"></i>
                              {t('filesTab.uploadingInProgress', 'Uploading files... Please wait.')}
                            </div>
                            <Progress animated color="info" value={100} style={{ height: '8px' }} />
                          </div>
                        )}

                        <button
                          type="button"
                          className="btn btn-sm btn-block mt-3"
                          onClick={handleUpload}
                          disabled={isUploading || !hasValidFiles}
                          style={{
                            ...(hasValidFiles && !isUploading ? activeColorStyle : {
                              background: '#6c757d',
                              backgroundImage: 'linear-gradient(to bottom left, #6c757d, #6c757d)',
                              borderColor: '#6c757d'
                            }),
                            color: 'white',
                            fontWeight: '600',
                            opacity: hasValidFiles && !isUploading ? 1 : 0.6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          {isUploading ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              <span>Uploading...</span>
                            </>
                          ) : (
                            hasValidFiles ? t('filesTab.uploadFilesButton', 'Upload Files') : t('filesTab.noValidFilesToUpload', 'No Valid Files')
                          )}
                        </button>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className={`${permission?.add_files ? 'col-md-8' : 'col-md-12'} mb-4`}>
        <div className="card">
          <div className="card-header font-weight-bold">{t('filesTab.fileGallery')}</div>
          <div className="card-body">
            {/* Real Media Data */}
            {data && data?.length > 0 ? (
              <div className="row">
                {data?.map((media) => (
                  <div className="col-md-12 mb-3" key={media.id}>
                    <div className="card" style={{
                      overflow: 'hidden',
                      boxShadow: isDarkMode
                        ? '0 1px 4px rgba(52, 70, 117, 0.1), 0 0 0 1px rgba(52, 70, 117, 0.05)'
                        : '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)',
                      transition: 'all 0.3s ease'
                    }}>
                      <div className="card-body p-3 text-center d-flex flex-row justify-content-between align-items-center">
                        <div className="d-flex flex-row align-items-center text-left">
                          <i
                            className={getFileIcon(media.media?.name || 'file')}
                            style={{
                              fontSize: '48px',
                              color: getFileIconColor(media.media?.name || 'file'),
                              marginBottom: '10px',
                              display: 'block'
                            }}
                          ></i>
                          <div className="ml-3 text-left" style={{ fontSize: "14px" }}>
                            {media.media?.name ? (
                              <div
                                className="mb-1 text-truncate"
                                style={{ color: fileTextStyles.name, fontWeight: 600, maxWidth: "420px" }}
                              >
                                {media.media.name}
                              </div>
                            ) : null}
                            {media.description ? (
                              <small className="d-block mb-1" style={{ color: fileTextStyles.info }}>
                                {media.description}
                              </small>
                            ) : null}
                            <small className="d-block" style={{ color: fileTextStyles.info, fontSize: "12px" }}>
                              {formatDateTimeForDisplay(media.created_date || media.createdAt || media.publishedAt)}
                              {media.created_user?.full_name && ` - ${media.created_user.full_name}`}
                            </small>
                          </div>
                        </div>
                        
                          



                        <div className="d-flex flex-row  align-items-center">
                           <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const fileUrl = toAppMediaAbsoluteUrl(media.media?.url || "");
                                if (!fileUrl) {
                                  showNotification("warning", t("filesTab.linkCopyFailed", "Could not copy link."));
                                  return;
                                }
                                const copied = await copyTextToClipboard(fileUrl);
                                if (copied) {
                                  showNotification(
                                    "success",
                                    t("filesTab.internalLinkCopied", "Internal link copied (login required).")
                                  );
                                } else {
                                  showNotification("warning", t("filesTab.linkCopyFailed", "Could not copy link."));
                                }
                              }}
                              style={{
                                fontSize: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                padding: 0,
                                marginRight: "8px",
                                backgroundColor: "#344767",
                                borderColor: "#344767",
                                backgroundImage: "none",
                                color: "#fff",
                              }}
                              title={t("filesTab.copyInternalLink", "Copy internal link (company only)")}
                            >
                              <i className="fas fa-link" style={{ color: "#fff" }}></i>
                            </button>
                           <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const shareUrl = await createExternalMediaShareUrl(media.media?.url || "");
                                  const copied = await copyTextToClipboard(shareUrl);
                                  if (copied) {
                                    showNotification(
                                      "success",
                                      t("filesTab.externalLinkCopied", "External share link copied.")
                                    );
                                  } else {
                                    showNotification("warning", t("filesTab.linkCopyFailed", "Could not copy link."));
                                  }
                                } catch {
                                  showNotification("warning", t("filesTab.linkCopyFailed", "Could not copy link."));
                                }
                              }}
                              style={{
                                fontSize: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                padding: 0,
                                marginRight: "8px",
                                backgroundColor: "#2dce89",
                                borderColor: "#2dce89",
                                backgroundImage: "none",
                                color: "#fff",
                              }}
                              title={t("filesTab.copyExternalLink", "Copy external link (anyone can open)")}
                            >
                              <i className="fas fa-share-alt" style={{ color: "#fff" }}></i>
                            </button>
                          {isPreviewableFile(media.media?.name || "") ? (
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openLightbox(media);
                              }}
                              style={{
                                fontSize: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                padding: 0,
                                marginRight: "8px",
                                backgroundColor: "#1d8cf8",
                                borderColor: "#1d8cf8",
                                backgroundImage: "none",
                                color: "white",
                              }}
                              title={t("filesTab.preview", "Preview")}
                            >
                              <i className="tim-icons icon-zoom-split" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const uploadPath = getMediaUploadPath(media);
                                const fileName = media.media?.name || "download";
                                if (!uploadPath) {
                                  showNotification(
                                    "warning",
                                    t("filesTab.previewFailed", "Could not load this file.")
                                  );
                                  return;
                                }
                                try {
                                  const { blobUrl } = await fetchSecureMediaBlobUrl(uploadPath);
                                  const link = document.createElement("a");
                                  link.href = blobUrl;
                                  link.download = fileName;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  URL.revokeObjectURL(blobUrl);
                                } catch {
                                  showNotification(
                                    "warning",
                                    t("filesTab.previewFailed", "Could not load this file.")
                                  );
                                }
                              }}
                              style={{
                                fontSize: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                padding: 0,
                                marginRight: "8px",
                                backgroundColor: "#1d8cf8",
                                borderColor: "#1d8cf8",
                                backgroundImage: "none",
                                color: "white",
                              }}
                              title={t("common.download", "Download")}
                            >
                              <i className="tim-icons icon-cloud-download-93" />
                            </button>
                          )}
                          {permission?.delete_files && (
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => handleDeleteMedia(media.id)}
                              disabled={deleteMediaMutation.isLoading}
                                style={{
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '32px',
                                  height: '32px',
                                  padding: 0,
                                  backgroundColor: '#dc3545',
                                  borderColor: '#dc3545',
                                  backgroundImage: 'none',
                                  color: 'white'
                                }}
                            >
                              <i className="tim-icons icon-trash-simple"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="row">
                <div className="col-md-12">
                  <div className="text-center">
                    <h5 className="card-title" style={{ color: fileTextStyles.heading }}>{t('filesTab.noFilesFound')}</h5>
                    <p style={{ color: fileTextStyles.muted }}>{t('filesTab.pleaseUploadFiles')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('filesTab.deleteFile')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>Are you sure you want to delete this file?</p>
          <p className='text-muted'>{t('filesTab.thisActionCannotBeUndone')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button color="secondary" type="button" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button
            style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteMediaMutation.isLoading}
          >
            {deleteMediaMutation.isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </Button>
        </div>
      </Modal>

      {lightboxOpen &&
        previewableFiles[lightboxIndex] &&
        ReactDOM.createPortal(
          <div
            onClick={closeLightbox}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.85)",
              zIndex: 10050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            {lightboxBlobUrl ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLightboxDownload();
                }}
                style={{
                  position: "absolute",
                  top: 16,
                  right: 72,
                  background: "rgba(0,0,0,0.55)",
                  border: "none",
                  color: "#fff",
                  width: 44,
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  cursor: "pointer",
                  zIndex: 10051,
                  transition: "transform 0.15s ease, background 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.12)";
                  e.currentTarget.style.background = "rgba(0,0,0,0.75)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.background = "rgba(0,0,0,0.55)";
                }}
                aria-label={t("common.download", "Download")}
                title={t("common.download", "Download")}
              >
                <i className="tim-icons icon-cloud-download-93" />
              </button>
            ) : null}

            <button
              type="button"
              onClick={closeLightbox}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "rgba(0,0,0,0.55)",
                border: "none",
                color: "#fff",
                width: 44,
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                cursor: "pointer",
                zIndex: 10051,
                transition: "transform 0.15s ease, background 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.12)";
                e.currentTarget.style.background = "rgba(0,0,0,0.75)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.background = "rgba(0,0,0,0.55)";
              }}
              aria-label={t("common.close", "Close")}
              title={t("common.close", "Close")}
            >
              <i className="tim-icons icon-simple-remove" />
            </button>

            {previewableFiles.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    prev === 0 ? previewableFiles.length - 1 : prev - 1
                  );
                }}
                style={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                  width: 44,
                  height: 44,
                  cursor: "pointer",
                  zIndex: 10051,
                }}
                aria-label={t("common.previous", "Previous")}
              >
                <i className="tim-icons icon-minimal-left" />
              </button>
            )}

            {previewableFiles[lightboxIndex].kind === "pdf" ||
            previewableFiles[lightboxIndex].kind === "image" ? (
              <FilesLightboxBody
                key={previewableFiles[lightboxIndex].uploadPath || lightboxIndex}
                item={previewableFiles[lightboxIndex]}
                isDarkMode={isDarkMode}
                t={t}
                onBlobUrlChange={setLightboxBlobUrl}
              />
            ) : null}

            {previewableFiles.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    prev === previewableFiles.length - 1 ? 0 : prev + 1
                  );
                }}
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                  width: 44,
                  height: 44,
                  cursor: "pointer",
                  zIndex: 10051,
                }}
                aria-label={t("common.next", "Next")}
              >
                <i className="tim-icons icon-minimal-right" />
              </button>
            )}

            <div
              style={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                textAlign: "center",
                maxWidth: "80vw",
              }}
            >
              <div>
                {lightboxIndex + 1} / {previewableFiles.length}
              </div>
              {previewableFiles[lightboxIndex].name ? (
                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 500,
                    opacity: 0.85,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {previewableFiles[lightboxIndex].name}
                </div>
              ) : null}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default FilesTab; 